import type { GoonDeckHardMaskConfig, HardMaskEffect, HardMaskTarget } from "./goondeck-foil-presets"

export interface HardMaskSourceCache {
  w: number
  h: number
  source: ImageData
  luminance: Float32Array
  /** Alpha channel of the card's pattern mesh (facet/dots/stars/grain/rain/
   * glitch/glisten), rasterized at the same size — lets the "Pattern"
   * target restrict the hard mask to wherever that mesh actually shows,
   * instead of a fixed geometric band like border/edge/art. Undefined when
   * the current pattern is "none" or the mesh hasn't loaded yet. */
  patternAlpha?: Float32Array
}

/** Rasterizes a pattern mesh (an SVG data: URL image, same one the CSS
 * mask-image uses) to get its per-pixel alpha for the "Pattern" hard-mask
 * target. Separate from prepareHardMaskSource since it's a different image
 * (the mesh, not the card art) and we only need its alpha channel. */
export function preparePatternAlpha(image: HTMLImageElement, w: number, h: number): Float32Array | null {
  if (!image.complete || !image.naturalWidth) return null
  const scratch = document.createElement("canvas")
  scratch.width = w
  scratch.height = h
  const ctx = scratch.getContext("2d", { willReadFrequently: true })
  if (!ctx) return null
  // The mesh's own viewBox already matches the card's aspect ratio (200x300
  // or 300x450 depending on the pattern) — stretch to fill, same as the CSS
  // mask-size: 100% 100% the real overlay uses, rather than cover-cropping.
  ctx.drawImage(image, 0, 0, w, h)
  const data = ctx.getImageData(0, 0, w, h).data
  const alpha = new Float32Array(w * h)
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) alpha[p] = data[i + 3]
  return alpha
}

/** Draws the card art to an offscreen canvas once per size change and
 * caches its pixel data + per-pixel luminance — the expensive part (a full
 * getImageData pass) only needs to happen again if the canvas is resized,
 * not on every pointer move. */
export function prepareHardMaskSource(image: HTMLImageElement, w: number, h: number): HardMaskSourceCache | null {
  if (!image.complete || !image.naturalWidth) return null
  const scratch = document.createElement("canvas")
  scratch.width = w
  scratch.height = h
  const ctx = scratch.getContext("2d", { willReadFrequently: true })
  if (!ctx) return null
  const scale = Math.max(w / image.naturalWidth, h / image.naturalHeight)
  const drawW = image.naturalWidth * scale
  const drawH = image.naturalHeight * scale
  ctx.drawImage(image, (w - drawW) / 2, (h - drawH) / 2, drawW, drawH)
  const source = ctx.getImageData(0, 0, w, h)
  const luminance = new Float32Array(w * h)
  for (let i = 0, p = 0; i < source.data.length; i += 4, p += 1) {
    luminance[p] = source.data[i] * 0.299 + source.data[i + 1] * 0.587 + source.data[i + 2] * 0.114
  }
  return { w, h, source, luminance }
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "")
  const value = parseInt(clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean, 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number, number] {
  h = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255, 255]
}

function effectColor(
  effect: HardMaskEffect,
  nx: number,
  ny: number,
  px: number,
  py: number,
  angle: number,
  selected: [number, number, number],
  sourcePixel: [number, number, number]
): [number, number, number, number] {
  if (effect === "custom") return [selected[0], selected[1], selected[2], 255]
  if (effect === "invert") return [255 - sourcePixel[0], 255 - sourcePixel[1], 255 - sourcePixel[2], 255]
  if (effect === "threshold") {
    // Real per-pixel luminance cutoff (Photoshop's Threshold), computed
    // directly from the actual card art pixel — not CSS backdrop-filter,
    // which turned out not to reliably see the plain art through this
    // component's layer stack. 127 matches Photoshop's own default split.
    const lum = sourcePixel[0] * 0.299 + sourcePixel[1] * 0.587 + sourcePixel[2] * 0.114
    const v = lum > 127 ? 255 : 0
    return [v, v, v, 255]
  }
  if (effect === "gold") {
    const grain = ((nx * 4000 + ny * 6000) % 19) / 19
    const shine = Math.max(0, 1 - Math.hypot(nx - px, ny - py) * 2.4)
    return [210 + shine * 45, 138 + grain * 88 + shine * 40, 26 + grain * 34, 255]
  }
  if (effect === "oil") {
    const band = Math.sin(nx * 12 - ny * 9 + (angle * Math.PI) / 90) * 0.5 + 0.5
    const hue = (angle + band * 210 + nx * 80) % 360
    return hslToRgb(hue, 0.98, 0.34 + band * 0.42)
  }
  if (effect === "polarized") {
    const band = Math.sin((nx * Math.cos((angle * Math.PI) / 180) + ny * Math.sin((angle * Math.PI) / 180)) * 58) * 0.5 + 0.5
    const hue = (angle + band * 260 + 190) % 360
    return hslToRgb(hue, 0.95, 0.58 + band * 0.22)
  }
  void selected
  return hslToRgb((angle + nx * 250 + ny * 180) % 360, 1, 0.62)
}

function targetAlpha(target: HardMaskTarget, nx: number, ny: number, patternAlpha: Float32Array | undefined, p: number): number {
  if (target === "full") return 1
  if (target === "pattern") {
    // No mesh loaded (pattern is "none", or it hasn't rasterized yet) —
    // fall back to the whole card rather than silently hiding the effect.
    if (!patternAlpha) return 1
    return patternAlpha[p] > 30 ? 1 : 0
  }
  if (target === "edge") return nx < 0.025 || nx > 0.975 || ny < 0.025 || ny > 0.975 ? 1 : 0
  if (target === "border") return nx < 0.075 || nx > 0.925 || ny < 0.075 || ny > 0.925 ? 1 : 0
  if (target === "layout") return nx < 0.075 || nx > 0.925 || ny < 0.075 || ny > 0.925 ? 1 : 0
  // Current GoonDeck cards are full-art cards: "art" means the printable
  // face, excluding only the tiny beveled edge so color leaks don't paint
  // outside the card surface.
  return nx >= 0.025 && nx <= 0.975 && ny >= 0.025 && ny <= 0.975 ? 1 : 0
}

/** Real per-pixel edge/luminance detection off the actual card art,
 * redrawn on pointer move — this is the expensive operation the whole
 * "inspect-view only" restriction exists for for. One canvas, ~1 frame of
 * work; never call this for more than one card at a time. */
export function drawHardMask(
  ctx: CanvasRenderingContext2D,
  cache: HardMaskSourceCache,
  config: GoonDeckHardMaskConfig,
  glareX: number,
  glareY: number,
  tiltAngle: number,
  tiltDistance: number,
) {
  const { w, h } = cache
  ctx.clearRect(0, 0, w, h)
  if (config.mode === "off") return

  const output = ctx.createImageData(w, h)
  const selected = hexToRgb(config.color)
  const px = glareX / 100
  const py = glareY / 100
  const distance = Math.min(1, tiltDistance / 50)
  const gateAngle = ((tiltAngle + 88) * Math.PI) / 180
  const gateNx = Math.cos(gateAngle)
  const gateNy = Math.sin(gateAngle)
  const gateWidth = 0.08 + distance * 0.22

  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const p = y * w + x
      const i = p * 4
      const nx = x / w
      const ny = y / h
      if (!targetAlpha(config.target, nx, ny, cache.patternAlpha, p)) continue

      const lum = cache.luminance[p]
      let mask = 0
      if (config.mode === "edges") {
        const gx =
          -cache.luminance[p - w - 1] - cache.luminance[p - 1] * 2 - cache.luminance[p + w - 1] +
          cache.luminance[p - w + 1] + cache.luminance[p + 1] * 2 + cache.luminance[p + w + 1]
        const gy =
          -cache.luminance[p - w - 1] - cache.luminance[p - w] * 2 - cache.luminance[p - w + 1] +
          cache.luminance[p + w - 1] + cache.luminance[p + w] * 2 + cache.luminance[p + w + 1]
        mask = Math.max(0, Math.min(255, (Math.hypot(gx, gy) - 34) * 4.4))
      } else if (config.mode === "dark") {
        mask = Math.max(0, Math.min(255, (96 - lum) * 4.2))
      } else if (config.mode === "bright") {
        mask = Math.max(0, Math.min(255, (lum - 178) * 3.2))
      } else if (config.mode === "color") {
        const dr = cache.source.data[i] - selected[0]
        const dg = cache.source.data[i + 1] - selected[1]
        const db = cache.source.data[i + 2] - selected[2]
        mask = Math.max(0, Math.min(255, (120 - Math.hypot(dr, dg, db)) * 2.7))
      } else if (config.mode === "full") {
        mask = 255
      }
      if (mask <= 0) continue

      const cx = nx - 0.5
      const cy = ny - 0.5
      const sweep = Math.max(0, 1 - Math.abs(cx * gateNx + cy * gateNy) / gateWidth)
      const glare = Math.max(0.18, 1 - Math.hypot(nx - px, ny - py) * 1.7)
      const alpha = Math.min(255, mask * (0.42 + sweep * 0.85) * glare * (distance * 0.8 + 0.45) * config.intensity)
      if (alpha <= 1) continue

      const color = effectColor(config.effect, nx, ny, px, py, tiltAngle, selected, [
        cache.source.data[i],
        cache.source.data[i + 1],
        cache.source.data[i + 2],
      ])
      output.data[i] = color[0]
      output.data[i + 1] = color[1]
      output.data[i + 2] = color[2]
      output.data[i + 3] = alpha
    }
  }
  ctx.putImageData(output, 0, 0)
}
